/**
 * SmsCreditWidget
 *
 * Displays current SMS credit balance, monthly usage, and a low-credit warning.
 * Renders in two modes:
 *   collapsed — icon only, badge if low
 *   expanded  — full balance bar with label
 */
import { CreditCard, AlertTriangle } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { useSmsCredits } from '../hooks'

interface SmsCreditWidgetProps {
  collapsed?: boolean
}

export function SmsCreditWidget({ collapsed = false }: SmsCreditWidgetProps) {
  const { data: credits } = useSmsCredits()

  if (!credits) return null

  const balancePct = credits.limit
    ? Math.min(Math.round((credits.balance / credits.limit) * 100), 100)
    : 100

  if (collapsed) {
    return (
      <div
        className="relative flex items-center justify-center p-2"
        title={`SMS Credits: ${credits.balance?.toLocaleString() ?? "0"} remaining`}
        aria-label={`SMS Credits: ${credits.balance?.toLocaleString() ?? "0"} remaining`}
      >
        <CreditCard
          className={cn(
            'h-5 w-5',
            credits.is_low ? 'text-yellow-500' : 'text-muted-foreground',
          )}
        />
        {credits.is_low && (
          <span
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-yellow-500 border border-background"
            aria-hidden
          />
        )}
      </div>
    )
  }

  return (
    <div className="rounded-md border bg-card px-3 py-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <CreditCard className="h-3.5 w-3.5" aria-hidden />
          SMS Credits
        </div>
        {credits.is_low && (
          <div className="flex items-center gap-1 text-yellow-600 text-xs font-medium">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            Low
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Progress
          value={balancePct}
          className={cn(
            'h-1.5',
            credits.is_low
              ? '[&>div]:bg-yellow-500'
              : '[&>div]:bg-primary',
          )}
          aria-label={`${balancePct}% of SMS credits remaining`}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{credits.balance?.toLocaleString() ?? "0"} remaining</span>
          {credits.limit !== null && (
            <span>of {credits.limit?.toLocaleString() ?? "0"}</span>
          )}
        </div>
      </div>

      {credits.used_this_month > 0 && (
        <p className="text-xs text-muted-foreground">
          {credits.used_this_month?.toLocaleString() ?? "0"} used this month
        </p>
      )}
    </div>
  )
}
